import type { Dialog } from '@reach/dialog';
import 'styles/node-modules/@reach/dialog/styles.css';
import classnames from 'classnames';
import React from 'react';
import { ModalSize } from './Modal';
import XSVG from './svg/XSVG';
import { Localizer } from 'services/Localizer';
import Spinner from './Spinner';
import { toKebabCase } from 'util/StringUtil';
const LazyDialog = React.lazy<typeof Dialog>(() =>
	import('@reach/dialog').then((module) => ({ default: module.Dialog })),
);

interface AlertProps {
	isOpen: boolean;
	renderTitle: () => React.ReactNode;
	children: React.ReactNode;
	onConfirm: () => void;
	onCancel: () => void;
	size?: ModalSize;
	theme?: AlertTheme;
	cancelButtonText?: string;
	confirmButtonText?: string;
	renderActions?: () => React.ReactNode;
	headerDivider?: boolean;
	subTitle?: string;
}

type AlertTheme = 'default' | 'info' | 'success' | 'warning' | 'danger';

interface ActionsRenderProps {
	theme?: AlertTheme;
	cancelButtonText?: string;
	confirmButtonText?: string;
	onConfirm: () => void;
	onCancel: () => void;
}

/**
 * Reusuable alert component with raised open state, takes size, theme, title and contents as props.
 *
 * @export
 * @param {AlertProps} props
 * @returns
 */
export default function Alert(props: AlertProps) {
	const {
		isOpen,
		size = 'md',
		theme = 'default',
		onConfirm,
		onCancel,
		renderTitle,
		cancelButtonText = Localizer('Cancel'),
		confirmButtonText = Localizer('Confirm'),
		renderActions = (renderProps: ActionsRenderProps) => (
			<>
				<button
					type="button"
					onClick={renderProps.onCancel}
					className="roc-btn roc-modal__btn roc-modal__btn-cancel"
					data-testid="roc-modal-btn-cancel"
				>
					{renderProps.cancelButtonText}
				</button>
				<button
					type="button"
					onClick={renderProps.onConfirm}
					className={classnames('roc-btn roc-modal__btn roc-modal__btn-confirm', {
						'roc-modal__btn-confirm--default': renderProps.theme === 'default',
						'roc-modal__btn-confirm--info': renderProps.theme === 'info',
						'roc-modal__btn-confirm--success': renderProps.theme === 'success',
						'roc-modal__btn-confirm--warning': renderProps.theme === 'warning',
						'roc-modal__btn-confirm--danger': renderProps.theme === 'danger',
					})}
					data-testid={`roc-modal-btn-confirm${
						renderProps.confirmButtonText ? '-' + toKebabCase(renderProps.confirmButtonText) : ''
					}`}
				>
					{renderProps.confirmButtonText}
				</button>
			</>
		),
		headerDivider = false,
		subTitle,
		children,
	} = props;

	return (
		<React.Suspense fallback={<Spinner />}>
			<LazyDialog
				isOpen={!!isOpen}
				className={classnames('roc-modal', {
					'roc-modal--xs': size === 'xs',
					'roc-modal--sm': size === 'sm',
					'roc-modal--md': size === 'md',
					'roc-modal--lg': size === 'lg',
					'roc-modal--xlg': size === 'xlg',
					'roc-modal--default': theme === 'default',
					'roc-modal--info': theme === 'info',
					'roc-modal--success': theme === 'success',
					'roc-modal--warning': theme === 'warning',
					'roc-modal--danger': theme === 'danger',
				})}
				onDismiss={() => onCancel()}
				as={'div'}
			>
				<div className={classnames('roc-modal__header', { 'roc-modal__header--divider': headerDivider })}>
					<div className="roc-modal__title-close-wrapper">
						<div className="roc-modal__title-container">
							<h2 className="roc-modal__title">{renderTitle()}</h2>
						</div>

						<div className="roc-modal__close">
							<button
								className="roc-modal__close-btn"
								onClick={() => onCancel()}
								type="button"
								data-testid="roc-modal-close-btn"
							>
								<span className="roc-modal__close-text">{Localizer('Close')}</span>
								<span className="roc-modal__close-icon">
									<XSVG />
								</span>
							</button>
						</div>
					</div>

					{subTitle ? <p className="roc_modal__subtitle">{subTitle}</p> : null}
				</div>
				<div className="roc-modal__body">{children}</div>
				<div className="roc-modal__footer">
					<div className="roc-modal__actions">
						{renderActions({ theme, cancelButtonText, confirmButtonText, onCancel, onConfirm })}
					</div>
				</div>
			</LazyDialog>
		</React.Suspense>
	);
}
